
import React, {
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  getMarkdownStringsForWidget,
  getStringsForWidget,
  widgetDefaultProps,
  widgetPropType,
} from '@glowforge/gf-styled/models';
import { track } from '../../util/analytics';
import ReferralInfoContext from '../../context/ReferralInfoContext';
import LocationContext from '../../context/LocationContext';
import useLocalStorage from '../../hooks/use-local-storage';

import FormStep from './steps-rebrand/contactForm';
import InterestsStep from './steps-rebrand/interests';
import ThankYouStep from './steps-rebrand/thank-you';
import './signup-rebrand.less';
import './signup-widget-rebrand.less';

/**
 * Storage key to persist state
 * Even though this isn't just the modal anymore,
 * keeping key name the same so users don't still see it
 * who have already completed forms before this change.
 */
const SUBMITTED_KEY = '_gf_signup_modal_submitted';

/**
 * Used to control when the user sees things like validation error messages
 */
const formStates = {
  READY: 'READY',
  CONTACT_COMPLETE: 'CONTACT_COMPLETE',
  SUBMITTED: 'SUBMITTED',
};

/**
 * Form steps control which screen the user sees
 */
const formSteps = {
  FORM: 'FORM',
  INTERESTS: 'INTERESTS',
  THANK_YOU: 'THANK_YOU',
};

/**
 * Get referral and promo link data out of referralInfo context object
 * @param {object} referralInfo ReferralInfo context
 */
const getAttributions = (referralInfo = {}, countryCode = '') => {
  const { code = '', pl: { id: promoLink = '' } = {} } = referralInfo;
  return { 
    attributions_referralCode: code, 
    attributions_promoLink: promoLink,
    attributions_countryCode: countryCode 
  };
};

/**
 * Find string with a specific prefix in Contentful data
 * @param {{ [key: string]: string }} strings
 * @param {string} string
 */
const getString = (strings, string) => (
  strings[`signup-modal-${string}`] || ''
);

/**
 * Shows a modal that accepts name, email and phone number followed by a thank
 * you message after submission
 */
const SignupWidget = (props) => {
  const strings = getStringsForWidget(props);
  const markdownStrings = getMarkdownStringsForWidget(props);

  const [submitted, setSubmitted] = useLocalStorage(SUBMITTED_KEY, false);

  const [formValues, setFormValues] = useState({});
  const [formStep, setFormStep] = useState(formSteps.FORM);
  const [formState, setFormState] = useState(formStates.READY);
  const { referralInfo, ensureReferralInfo } = useContext(ReferralInfoContext);
  const { country: countryCode } = useContext(LocationContext);

  /**
   * When grabbing images from contentful, these will come back in the order
   * they are added in contentful. So if you add another one, keep that in mind.
   * These have to live up here because of this instead of in their
   * children components.
  */
  const {
    images: [
      leftColImage,
      rightColImage,
      brandBgImage,
      bottomInterestsImage,
      thankYouImage,
    ] = [],
    onClose,
  } = props;

  /**
   * Server side rendering issue solution
   * If you try to set these outside of the useEffect that's watching submitted
   * then the server will believe the form step is .FORM because it won't have
   * access to the value in localstorage to check.
   */
  useEffect(() => {
    setFormState(submitted ? formStates.SUBMITTED : formStates.READY);
    setFormStep(submitted ? formSteps.THANK_YOU : formSteps.FORM);
  }, [submitted]);

  /**
   * When component first renders, fetch referral info
   */
  useEffect(() => {
    ensureReferralInfo();
  }, []);

  const onSubmitEmail = () => {
    if (window.analytics) {
      window.analytics.identify(formValues);
      window.analytics.track('madlib_email_info_submitted', {
        id: 'signup-widget',
        ...getAttributions(referralInfo, countryCode),
        ...formValues,
      });
    }
  }

  /**
   * Collect form response and transition to thank you page
   * @param {FormEvent<HTMLFormElement>} e
   */
  const onSubmitInterest = (interest) => {
    setFormState(formStates.SUBMITTED);

    const formValuesWithInterest = {
      ...formValues,
      interest: interest || getString(strings, 'not-sure-label') || '',
    };

    if (window.analytics) {
      window.analytics.identify(formValuesWithInterest);
      window.analytics.track('madlib_email_submitted', {
        id: 'signup-widget',
        ...getAttributions(referralInfo, countryCode),
        ...formValuesWithInterest,
      });
    }

    setSubmitted(true);
  };


  let currentStep = null;

  switch (formStep) {
    case formSteps.FORM:
      currentStep = (
        <FormStep
        key='info-step-widget'
        getString={getString}
        formState={formState}
        formValues={formValues}
        images={{
          leftColImage,
          rightColImage,
        }}
        nextFormState={formStates.CONTACT_COMPLETE}
        nextFormStep={formSteps.INTERESTS}
        setFormState={setFormState}
        setFormStep={setFormStep}
        setFormValues={setFormValues}
        strings={strings}
        onSubmit={onSubmitEmail}
        track={track} />
      );
      break;
    case formSteps.INTERESTS:
      currentStep = (
        <InterestsStep
          key='interest-step-widget'
          getString={getString}
          images={{
            brandBgImage,
            bottomInterestsImage,
          }}
          nextFormStep={formSteps.THANK_YOU}
          onSubmit={onSubmitInterest}
          setFormStep={setFormStep}
          strings={strings}
          track={track} />
      );
      break;
    case formSteps.THANK_YOU:
      currentStep = (
        <ThankYouStep
          key='thank-you-step-widget'
          getString={getString}
          markdownStrings={markdownStrings}
          onClose={onClose}
          strings={strings}
          thankYouImage={thankYouImage} />
      );
      break;
    default:
      currentStep = null;
  }

  return (
    <div className='SignupWidget'>
      {currentStep}
    </div>
  );
};

SignupWidget.propTypes = widgetPropType;
SignupWidget.defaultProps = widgetDefaultProps;

export default SignupWidget;
